import { AttrType, CustomAttribute, Node, SetAttributeValuesNode } from '@/api/dashboard'
import { BlockError, DATE_TIME_NOW_ATTRIBUTE_VALUE } from '../types'
import SettingsHeader from '@/layouts/campaigns/SettingsHeader'
import { Controller, FieldArray, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { Button, Select, SelectValue, Textarea, Trash, cn, useModal } from '@dashboard/ui'
import { useGenerateCompanyPath } from '@/libs'
import { PlayerAttributeValueEditor } from '@/layouts/player/components/PlayerAttributeValueEditor'
import { AddButton } from '@/layouts/components/AddButton'
import { ButtonIcon, FieldGroup, FieldSection, TableZeroState } from '@/ui'
import { Plus } from '@/icons'
import EditGameAttrModal from '@/layouts/player/EditGameAttrModal'
import { useGameSettingsUpdateMutate } from '@/api/useGameSettingsUpdateMutate'
import { useContext, useState } from 'react'
import { CampaignContext, ICampaignContext } from '@/expr/Context'
import { getDisabledClasses } from '@/layouts/campaigns/util'
import { TokenValue } from '@/expr/types'
import { Divider } from '@/components'

type Value = string | number | boolean | null

interface FormData {
  values: {
    name: string
    value: Value
  }[]
}

export const SetUserAttributeEditor = (props: {
  node: SetAttributeValuesNode
  error?: BlockError | null
  setNode?: (node: Node) => void
  onClose: () => void
}) => {
  const context = useContext(CampaignContext) as ICampaignContext
  const { companyId, gameId } = useGenerateCompanyPath()
  const { t } = useTranslation()
  const { data: gameSettings } = useGameSettingsQuery(companyId, gameId)
  const { mutateAsync: updateMutateAsync } = useGameSettingsUpdateMutate(companyId, gameId)
  const [description, setDescription] = useState(props.node.description || '')

  const nodeValues = props.node?.values as Record<string, Value>
  const arr = []
  for (const key in nodeValues) {
    arr.push({
      name: key,
      value: nodeValues[key],
    })
  }

  const { handleSubmit, reset, control, watch, setValue } = useForm<FormData>({
    values: {
      values: arr,
    },
  })

  const values = useFieldArray({
    control,
    name: 'values',
  })

  const openEditModal = useModal<{ item: CustomAttribute; isNew: boolean }>(props => (
    <EditGameAttrModal
      {...props}
      onSave={async item => {
        if (!gameSettings) {
          return
        }
        await updateMutateAsync({
          settingsId: gameSettings.id,
          data: {
            ...gameSettings,
            player_attributes: [...(gameSettings.player_attributes || []), item],
          },
        })
        values.append({
          name: item.name,
          value: null,
        })
      }}
    />
  ))

  const onSubmit = (data: FormData) => {
    const objValues: Record<string, Value> = {}
    for (const item of data.values) {
      if (item.name) {
        objValues[item.name] = item.value
      }
    }

    if (props.setNode) {
      props.setNode({
        ...props.node,
        values: objValues,
        description: description,
      } as unknown as Node)
    }
    reset({ values: [] })
    props.onClose()
  }

  const noAddedAttributes =
    gameSettings?.player_attributes?.filter(attr => watch(`values`).find(item => item.name === attr.name) == null) || []

  const renderZeroState = () => {
    return (
      <TableZeroState
        contentClassName="py-10"
        title={t('player.attr.zero.title')}
        message={t('player.attr.zero.message')}
        buttons={
          <Button
            size="sm"
            onClick={() =>
              openEditModal({
                item: {
                  name: '',
                  description: '',
                  type: AttrType.String,
                },
                isNew: true,
              })
            }
          >
            <Plus size={16} />
            <span>{t('player.attr.zero.button-add')}</span>
          </Button>
        }
      />
    )
  }

  const renderRow = (item: FieldArray<FormData, 'values'> & Record<'id', string>, index: number) => {
    const attrType = gameSettings?.player_attributes?.find(attr => attr.name === watch(`values.${index}.name`))?.type

    return (
      <div key={item.name} className="flex gap-2">
        <div className="w-1/2">
          <Controller
            name={`values.${index}.name`}
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={v => {
                  field.onChange(v)
                  if (gameSettings?.player_attributes?.find(attr => attr.name == v)?.type == AttrType.Date) {
                    setValue(`values.${index}.value`, DATE_TIME_NOW_ATTRIBUTE_VALUE)
                  }
                }}
                items={
                  gameSettings?.player_attributes?.map(attribute => ({
                    children: attribute.name,
                    value: attribute.name,
                  })) || []
                }
              />
            )}
          />
        </div>

        <div className="w-1/2">
          <Controller
            name={`values.${index}.value`}
            control={control}
            render={({ field }) =>
              attrType == AttrType.Date ? (
                <Select
                  value={field.value as SelectValue}
                  items={[
                    {
                      children: t('campaign.set-attr-editor.now'),
                      value: DATE_TIME_NOW_ATTRIBUTE_VALUE,
                      caption: t('campaign.set-attr-editor.now.desc'),
                    },
                  ]}
                  onChange={value => {
                    field.onChange(value)
                  }}
                />
              ) : (
                <PlayerAttributeValueEditor
                  type={attrType || AttrType.String}
                  value={field.value as TokenValue}
                  onChange={value => {
                    field.onChange(value)
                  }}
                />
              )
            }
          />
        </div>
        <ButtonIcon variant="tertiary" color="danger" onClick={() => values.remove(index)}>
          <Trash />
        </ButtonIcon>
      </div>
    )
  }

  return (
    <>
      <SettingsHeader
        onSave={handleSubmit(onSubmit)}
        onClose={() => {
          reset({ values: [] })
          props.onClose()
        }}
        text={t('campaign.block.SetAttributeValuesNode.name')}
      />
      <div className={cn('p-6', getDisabledClasses(context.readOnly))}>
        <FieldSection
          label={t('campaign.node-type.SetAttributeValuesNode')}
          caption={t('campaign.set-attr-editor.desc')}
        >
          <div>
            <div className="flex flex-col gap-4">
              {values.fields.map((item, index) => renderRow(item, index))}
              {(gameSettings?.player_attributes || []).length > 0 && (
                <AddButton
                  size="xs"
                  onClick={() => {
                    if (noAddedAttributes.length == 0) {
                      openEditModal({
                        item: {
                          name: '',
                          description: '',
                          type: AttrType.String,
                        },
                        isNew: true,
                      })
                    } else {
                      values.append({
                        name: '',
                        value: null,
                      })
                    }
                  }}
                >
                  {t('Add')}
                </AddButton>
              )}

              {gameSettings?.player_attributes?.length == 0 && renderZeroState()}
            </div>
          </div>
        </FieldSection>
        <Divider />
        <FieldSection label={t('campaign.description')}>
          <FieldGroup label={t('campaign.block.description.tooltip')}>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} />
          </FieldGroup>
        </FieldSection>
      </div>
    </>
  )
}
